class StickyATC extends HTMLElement {
  constructor() {
    super();
    this.isSyncingQty = false;
    this.isFooterVisible = false;
    this.isMediaPastThreshold = false;
    this.isActive = false;
    this.isDesktop = false;
    this.breakpointMql = null;
    this.enableOnMobile = true;
    this.enableOnDesktop = true;
    this.handleFooterIntersection = this.handleFooterIntersection.bind(this);
    this.handleMediaIntersection = this.handleMediaIntersection.bind(this);
    this.updateStickyVisibility = this.updateStickyVisibility.bind(this);
    this.onProductMediaUpdated = this.onProductMediaUpdated.bind(this);
    this.updateProductInfo = this.updateProductInfo.bind(this);
    this.onStickySwatchClick = this.updateMainProductColorVariant.bind(this);
    this.onQtyButtonClick = this.onQtyButtonClick.bind(this);
    this.onQtyInputChange = this.onQtyInputChange.bind(this);
    this.onMainQtyChange = this.onMainQtyChange.bind(this);
    this.onStickyAddToCartClick = this.onStickyAddToCartClick.bind(this);
    this.onBreakpointChange = this.onBreakpointChange.bind(this);
    this.onSellingPlanInputChange = this.onSellingPlanInputChange.bind(this);
    this.onOptionsToggle = this.onOptionsToggle.bind(this);
    this.onCartRequestEnd = this.onCartRequestEnd.bind(this);
    this.comparePriceText = null;
    this.hasBeenViewed = false;
    this.isStickyAtcInFlight = false;
  }

  dispatchQtyEvents(input) {
    if (!input) return;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  pushDL(eventName, data) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: eventName }, data));
  }

  connectedCallback() {
    this.enableOnMobile = this.getAttribute('data-enable-mobile') !== 'false';
    this.enableOnDesktop = this.getAttribute('data-enable-desktop') !== 'false';

    this.breakpointMql = window.matchMedia('(min-width: 768px)');
    this.onBreakpointChange();

    if (this.breakpointMql.addEventListener) {
      this.breakpointMql.addEventListener('change', this.onBreakpointChange);
    } else if (this.breakpointMql.addListener) {
      this.breakpointMql.addListener(this.onBreakpointChange);
    }
  }

  disconnectedCallback() {
    this.disable();

    if (this.breakpointMql) {
      if (this.breakpointMql.removeEventListener) {
        this.breakpointMql.removeEventListener('change', this.onBreakpointChange);
      } else if (this.breakpointMql.removeListener) {
        // Safari < 14 fallback
        this.breakpointMql.removeListener(this.onBreakpointChange);
      }
    }
    this.breakpointMql = null;

    // Keep these removes to be safe even if we were never enabled.
    this.footerObserver?.disconnect();
    this.mediaObserver?.disconnect();
    this.atcObserver?.disconnect();
    this.atcTextObserver?.disconnect();
    this.atcTextObserver = null;

    document.removeEventListener('product-media::updated', this.onProductMediaUpdated);
    document.removeEventListener('renderSection:complete', this.updateProductInfo);

    this.stickyAddToCartBtn?.removeEventListener('click', this.onStickyAddToCartClick);
    this.qtyPlusBtn?.removeEventListener('click', this.onQtyButtonClick);
    this.qtyMinusBtn?.removeEventListener('click', this.onQtyButtonClick);
    this.qtyInput?.removeEventListener('change', this.onQtyInputChange);
    this.mainQtyInput?.removeEventListener('change', this.onMainQtyChange);
    this.stickyVariantSwatches?.forEach(pill =>
      pill.removeEventListener('click', this.onStickySwatchClick)
    );
    this.optionsDetails?.removeEventListener('toggle', this.onOptionsToggle);
    document.removeEventListener('liquid-ajax-cart:request-end', this.onCartRequestEnd);
  }

  onBreakpointChange() {
    const isDesktop = (this.breakpointMql?.matches === true);
    this.isDesktop = isDesktop;
    const shouldEnable = isDesktop ? this.enableOnDesktop : this.enableOnMobile;

    if (shouldEnable) {
      this.enable();
      this.enforceOptionsDetailsState();
      return;
    }

    this.disable();
  }

  enable() {
    if (this.isActive) {
      this.hidden = false;
      this.cacheElements();
      this.enforceOptionsDetailsState();
      return;
    }

    this.isActive = true;
    this.hidden = false;

    this.cacheElements();
    this.comparePriceText = this.getCompareTextFromDataset();
    this.initFooterObserver();
    this.initMediaObserver();
    this.initQuantitySelector();
    this.initAddToCartObserver();
    this.syncStickyAtcTextAndPrice();
    this.enforceOptionsDetailsState();
    this.syncStickyOptionPillsFromMainSelector();

    document.addEventListener('product-media::updated', this.onProductMediaUpdated);
    document.addEventListener('renderSection:complete', this.updateProductInfo);

    this.mainSellingPlanInput?.removeEventListener('change', this.onSellingPlanInputChange);
    this.mainSellingPlanInput?.addEventListener('change', this.onSellingPlanInputChange);

    this.stickyAddToCartBtn?.addEventListener('click', this.onStickyAddToCartClick);
    this.stickyVariantSwatches?.forEach((pill) => pill.addEventListener('click', this.onStickySwatchClick));

    this.optionsDetails?.addEventListener('toggle', this.onOptionsToggle);
    document.addEventListener('liquid-ajax-cart:request-end', this.onCartRequestEnd);
  }

  disable() {
    // Always hide when disabled for the current breakpoint.
    this.hidden = true;
    this.classList.remove('sticky-atc--visible');

    if (!this.isActive) {
      return;
    }
    this.isActive = false;

    this.footerObserver?.disconnect();
    this.mediaObserver?.disconnect();
    this.atcObserver?.disconnect();
    this.atcTextObserver?.disconnect();
    this.atcTextObserver = null;

    document.removeEventListener('product-media::updated', this.onProductMediaUpdated);
    document.removeEventListener('renderSection:complete', this.updateProductInfo);
    this.mainSellingPlanInput?.removeEventListener('change', this.onSellingPlanInputChange);

    this.stickyAddToCartBtn?.removeEventListener('click', this.onStickyAddToCartClick);
    this.qtyPlusBtn?.removeEventListener('click', this.onQtyButtonClick);
    this.qtyMinusBtn?.removeEventListener('click', this.onQtyButtonClick);
    this.qtyInput?.removeEventListener('change', this.onQtyInputChange);
    this.mainQtyInput?.removeEventListener('change', this.onMainQtyChange);
    this.stickyVariantSwatches?.forEach((pill) => pill.removeEventListener('click', this.onStickySwatchClick));

    this.optionsDetails?.removeEventListener('toggle', this.onOptionsToggle);
    document.removeEventListener('liquid-ajax-cart:request-end', this.onCartRequestEnd);
  }

  cacheElements() {
    this.stickyImage = this.querySelector('.sticky-atc__product-media-wrapper img');
    this.footer = document.querySelector('#shopify-section-main-footer footer');
    this.stickyAtcText = this.querySelector('.sticky-atc__add-to-cart-text');
    this.stickyAtcPrice = this.querySelector('.sticky-atc__add-to-cart-price');
    this.stickyAtcPriceCurrent = this.querySelector('[data-sticky-price-current]');
    this.stickyAtcPriceInterval = this.querySelector('[data-sticky-price-interval]');
    this.stickyAtcPriceCompare = this.querySelector('[data-sticky-price-compare]');
    this.stickyAtcPriceCompareS = this.querySelector('[data-sticky-price-compare-s]');
    this.sectionId = this.getAttribute("data-section-id");
    this.productFormId = this.getAttribute("data-product-form-id");
    this.isThisBundle = (this.getAttribute("data-is-it-bundle") === "true");
    this.mainProductInfo = document.querySelector(`product-info[data-section="${this.sectionId}"]`);
    this.mainProductMediaWrapper = this.mainProductInfo?.querySelector('.main-product-media-wrapper');
    this.stickyVariantSwatches = this.querySelectorAll(".sticky-atc__variant-pill");
    this.mainProductVariantSelector = document.querySelector(`#variant-selector-${this.sectionId}`);
    this.mainQtyInput = document.querySelector(`#Quantity-${this.sectionId}`);
    this.qtyInput = this.querySelector('[data-qty-input]');
    this.qtyPlusBtn = this.querySelector('[data-qty-plus]');
    this.qtyMinusBtn = this.querySelector('[data-qty-minus]');
    this.mainProductForm = document.querySelector(`form[id='${this.productFormId}']`);
    this.mainSellingPlanInput =
      document.querySelector(`input.selected-selling-plan-id[form='${this.productFormId}']`) ||
      this.mainProductForm?.querySelector('input.selected-selling-plan-id') ||
      document.querySelector('.selected-selling-plan-id');
    this.mainAddToCartBtn = this.isThisBundle ? document.querySelector('#add-bundle-item-to-cart') : this.mainProductForm?.querySelector('button[name="add"]');
    this.stickyAddToCartBtn = this.querySelector('[data-sticky-atc]');
    this.ajaxCartForm = document.querySelector('product-info .product-form__buttons ajax-cart-product-form');
    this.stickySpinner = this.querySelector('[data-sticky-spinner]');
    this.optionsDetails = this.querySelector('.sticky-atc__options-toggle');
    this.initialVariantPriceCents = parseInt(this.getAttribute('data-initial-variant-price') || '0', 10);
    this.initialVariantCompareAtPriceCents = parseInt(this.getAttribute('data-initial-variant-compare-at-price') || '0', 10);
    this.productId = this.getAttribute('data-product-id') || null;
    this.productTitle = this.getAttribute('data-product-title') || null;
  }

  getProductData() {
    return {
      product_id: this.productId,
      product_title: this.productTitle,
    };
  }

  getSelectedVariantData() {
    try {
      var el = this.mainProductInfo?.querySelector('[data-selected-variant]');
      if (el) return JSON.parse(el.textContent);
    } catch (_) { }
    return null;
  }

  getVariantData() {
    var v = this.getSelectedVariantData();
    var displayedPrice = this.stickyAtcPriceCurrent?.textContent?.trim() || null;
    if (v) {
      return {
        variant_id: v.id || null,
        variant_title: v.title || null,
        price: displayedPrice,
      };
    }
    var vid = this.mainProductForm?.querySelector('input[name="id"]')?.value || null;
    return { variant_id: vid, variant_title: null, price: displayedPrice };
  }

  getCompareTextFromDataset() {
    const price = Number.isFinite(this.initialVariantPriceCents) ? this.initialVariantPriceCents : null;
    const compareAtPrice = Number.isFinite(this.initialVariantCompareAtPriceCents)
      ? this.initialVariantCompareAtPriceCents
      : null;

    return this.getCompareTextFromVariant({ price, compareAtPrice });
  }

  getCompareTextFromVariant({ price, compareAtPrice }) {
    const safePrice = typeof price === 'number' ? price : null;
    const safeCompareAt = typeof compareAtPrice === 'number' ? compareAtPrice : null;

    // Match Skio plan picker compare behavior: prefer compare_at_price if present, else fall back to price.
    const compareCents = safeCompareAt && safeCompareAt > 0 ? safeCompareAt : safePrice;
    if (!compareCents || compareCents <= 0) return null;

    return this.formatMoney(compareCents);
  }

  formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, window.Shopify.money_format);
    }
    return `$${(cents / 100).toFixed(2)}`;
  }

  isSubscriptionSelected() {
    return !!(this.mainSellingPlanInput && String(this.mainSellingPlanInput.value || '').trim());
  }

  setStickyPriceUI({ currentText, isSubscription, compareText }) {
    if (!this.stickyAtcPriceCurrent || !this.stickyAtcPriceInterval || !this.stickyAtcPriceCompare || !this.stickyAtcPriceCompareS) {
      if (this.stickyAtcPrice) this.stickyAtcPrice.textContent = String(currentText || '').trim();
      return;
    }

    const safeCurrent = String(currentText || '').trim();
    const safeCompare = String(compareText || '').trim();
    const showCompare = !!(safeCompare && safeCurrent && safeCompare !== safeCurrent);

    this.stickyAtcPriceCurrent.textContent = safeCurrent;
    this.stickyAtcPriceInterval.textContent = isSubscription ? '/mo' : '';

    this.stickyAtcPriceCompareS.textContent = safeCompare;
    this.stickyAtcPriceCompare.toggleAttribute('hidden', !showCompare);
  }

  syncStickyOptionPillsFromMainSelector() {
    if (!this.mainProductVariantSelector) return;

    const optionButtons = this.querySelectorAll('.sticky-atc__variant-pill--size, .sticky-atc__variant-pill--quantity');
    optionButtons.forEach(btn => {
      const inputId = btn.getAttribute('data-input-id');
      if (!inputId) return;

      const srcInput = this.mainProductVariantSelector.querySelector(`#${inputId}`);
      if (!srcInput) return;

      const isDisabled = srcInput.classList.contains('disabled') || srcInput.disabled === true;
      const isQuantityPill = btn.classList.contains('sticky-atc__variant-pill--quantity');
      const shouldHideBecauseUnselected = isQuantityPill && srcInput.checked !== true;

      btn.classList.toggle('hidden', isDisabled || shouldHideBecauseUnselected);

      const shouldBeActive = srcInput.checked === true;

      // Only clear siblings when we're about to activate a new one.
      const optionPosition = btn.getAttribute('data-option-position');
      if (shouldBeActive && optionPosition && !btn.classList.contains('is-active')) {
        this.querySelectorAll(`.sticky-atc__variant-pill[data-option-position="${optionPosition}"]`).forEach(p =>
          p.classList.remove('is-active')
        );
      }

      btn.classList.toggle('is-active', shouldBeActive);
    });
  }

  onOptionsToggle() {
    if (this.optionsDetails?.open) {
      this.pushDL('sticky_options_open', this.getProductData());
    }
  }

  enforceOptionsDetailsState() {
    if (!this.optionsDetails) return;
    this.optionsDetails.open = this.isDesktop;
  }

  getProductData() {
    return {
      product_id: this.productId,
      product_title: this.productTitle,
    };
  }

  getSelectedVariantData() {
    try {
      var el = this.mainProductInfo?.querySelector('[data-selected-variant]');
      if (el) return JSON.parse(el.textContent);
    } catch (_) { }
    return null;
  }

  getVariantData() {
    var v = this.getSelectedVariantData();
    var displayedPrice = this.stickyAtcPriceCurrent?.textContent?.trim() || null;
    if (v) {
      return {
        variant_id: v.id || null,
        variant_title: v.title || null,
        price: displayedPrice,
      };
    }
    var vid = this.mainProductForm?.querySelector('input[name="id"]')?.value || null;
    return { variant_id: vid, variant_title: null, price: displayedPrice };
  }

  getCompareTextFromDataset() {
    const price = Number.isFinite(this.initialVariantPriceCents) ? this.initialVariantPriceCents : null;
    const compareAtPrice = Number.isFinite(this.initialVariantCompareAtPriceCents)
      ? this.initialVariantCompareAtPriceCents
      : null;

    return this.getCompareTextFromVariant({ price, compareAtPrice });
  }

  getCompareTextFromVariant({ price, compareAtPrice }) {
    const safePrice = typeof price === 'number' ? price : null;
    const safeCompareAt = typeof compareAtPrice === 'number' ? compareAtPrice : null;

    // Match Skio plan picker compare behavior: prefer compare_at_price if present, else fall back to price.
    const compareCents = safeCompareAt && safeCompareAt > 0 ? safeCompareAt : safePrice;
    if (!compareCents || compareCents <= 0) return null;

    return this.formatMoney(compareCents);
  }

  formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, window.Shopify.money_format);
    }
    return `$${(cents / 100).toFixed(2)}`;
  }

  isSubscriptionSelected() {
    if (this.mainSellingPlanInput && String(this.mainSellingPlanInput.value || '').trim()) {
      return true;
    }
    var label = this.mainProductForm?.querySelector('.add-to-cart-label');
    if (label && label.textContent.trim().toLowerCase().includes('subscribe')) {
      return true;
    }
    return false;
  }

  setStickyPriceUI({ currentText, isSubscription, compareText }) {
    if (!this.stickyAtcPriceCurrent || !this.stickyAtcPriceInterval || !this.stickyAtcPriceCompare || !this.stickyAtcPriceCompareS) {
      if (this.stickyAtcPrice) this.stickyAtcPrice.textContent = String(currentText || '').trim();
      return;
    }

    const safeCurrent = String(currentText || '').trim();
    const safeCompare = String(compareText || '').trim();
    const showCompare = !!(safeCompare && safeCurrent && safeCompare !== safeCurrent);

    this.stickyAtcPriceCurrent.textContent = safeCurrent;
    this.stickyAtcPriceInterval.textContent = isSubscription ? '/mo' : '';

    this.stickyAtcPriceCompareS.textContent = safeCompare;
    this.stickyAtcPriceCompare.toggleAttribute('hidden', !showCompare);
  }

  syncStickyOptionPillsFromMainSelector() {
    if (!this.mainProductVariantSelector) return;

    const optionButtons = this.querySelectorAll('.sticky-atc__variant-pill--size, .sticky-atc__variant-pill--quantity');
    optionButtons.forEach(btn => {
      const inputId = btn.getAttribute('data-input-id');
      if (!inputId) return;

      const srcInput = this.mainProductVariantSelector.querySelector(`#${inputId}`);
      if (!srcInput) return;

      const isDisabled = srcInput.classList.contains('disabled') || srcInput.disabled === true;
      const isQuantityPill = btn.classList.contains('sticky-atc__variant-pill--quantity');
      const shouldHideBecauseUnselected = isQuantityPill && srcInput.checked !== true;

      btn.classList.toggle('hidden', isDisabled || shouldHideBecauseUnselected);

      const shouldBeActive = srcInput.checked === true;

      // Only clear siblings when we're about to activate a new one.
      const optionPosition = btn.getAttribute('data-option-position');
      if (shouldBeActive && optionPosition && !btn.classList.contains('is-active')) {
        this.querySelectorAll(`.sticky-atc__variant-pill[data-option-position="${optionPosition}"]`).forEach(p =>
          p.classList.remove('is-active')
        );
      }

      btn.classList.toggle('is-active', shouldBeActive);
    });
  }

  onOptionsToggle() {
    if (this.optionsDetails?.open) {
      this.pushDL('sticky_options_open', this.getProductData());
    }
  }


  initFooterObserver() {
    this.footerObserver?.disconnect();
    this.footerObserver = null;

    if (!this.footer) {
      this.isFooterVisible = false;
      this.updateStickyVisibility();
      return;
    }

    this.footerObserver = new IntersectionObserver(
      this.handleFooterIntersection,
      { root: null, threshold: 0 }
    );
    this.footerObserver.observe(this.footer);
  }

  initMediaObserver() {
    this.mediaObserver?.disconnect();
    this.mediaObserver = null;

    if (!this.mainProductMediaWrapper) {
      this.isMediaPastThreshold = false;
      this.updateStickyVisibility();
      return;
    }

    // "Scrolled 80%" ≈ media is <= 20% visible in the viewport.
    this.mediaObserver = new IntersectionObserver(this.handleMediaIntersection, {
      root: null,
      threshold: [0, 0.2, 1],
    });

    this.mediaObserver.observe(this.mainProductMediaWrapper);
  }

  updateStickyVisibility() {
    const shouldShowSticky = !this.isFooterVisible && this.isMediaPastThreshold;
    this.classList.toggle('sticky-atc--visible', shouldShowSticky);

    if (shouldShowSticky && !this.hasBeenViewed) {
      this.hasBeenViewed = true;
      this.pushDL('sticky_atc_view', this.getProductData());
    }
  }

  isMainAtcProcessing() {
    if (this.isThisBundle && this.mainAddToCartBtn) {
      return (
        this.mainAddToCartBtn.hasAttribute('processing') ||
        this.mainAddToCartBtn.disabled ||
        this.mainAddToCartBtn.getAttribute('aria-busy') === 'true'
      );
    }

    if (!this.isThisBundle && this.ajaxCartForm) {
      return (
        this.ajaxCartForm.hasAttribute('processing') ||
        this.ajaxCartForm.getAttribute('aria-busy') === 'true'
      );
    }

    return false;
  }


  initAddToCartObserver() {
    if (!this.stickyAddToCartBtn) return;

    const updateStickyState = () => {
      const isProcessing = this.isMainAtcProcessing();

      this.stickyAddToCartBtn.disabled = isProcessing;
      this.stickyAddToCartBtn.setAttribute('updating', isProcessing);
      this.stickySpinner?.classList.toggle('hidden', !isProcessing);
    };

    this.atcObserver = new MutationObserver(updateStickyState);

    const target = this.isThisBundle ? this.mainAddToCartBtn : this.ajaxCartForm;

    if (!target) return;

    this.atcObserver.observe(target, {
      attributes: true,
      attributeFilter: ['processing', 'aria-busy', 'disabled'],
    });

    updateStickyState();
  }



  onProductMediaUpdated(event) {
    const eventSectionId = event.detail?.sectionId;
    if (eventSectionId && this.sectionId && eventSectionId !== this.sectionId) return;

    const slide = event.detail?.firstSlide?.querySelector('img:not(.product-media-gallery__badge-image)');
    if (!slide || !this.stickyImage) return;
    this.stickyImage.src = slide.currentSrc || slide.src;
    if (slide.alt) this.stickyImage.alt = slide.alt;
  }

  updateQtyButtons() {
    if (!this.qtyInput) return;
    const value = parseInt(this.qtyInput.value, 10);
    this.qtyMinusBtn && (this.qtyMinusBtn.disabled = value <= this.minQty);
    this.qtyPlusBtn && this.maxQty !== null && (this.qtyPlusBtn.disabled = value >= this.maxQty);
  }

  onMainQtyChange() {
    if (!this.qtyInput || !this.mainQtyInput || this.isSyncingQty) return;
    const value = parseInt(this.mainQtyInput.value, 10);
    if (isNaN(value)) return;

    this.isSyncingQty = true;
    this.qtyInput.value = value;
    this.updateQtyButtons();
    this.isSyncingQty = false;
  }

  onQtyInputChange() {
    if (!this.qtyInput || !this.mainQtyInput || this.isSyncingQty) return;

    let value = parseInt(this.qtyInput.value, 10);
    if (isNaN(value)) value = this.minQty;
    if (this.maxQty !== null) value = Math.min(value, this.maxQty);
    value = Math.max(value, this.minQty);

    this.isSyncingQty = true;
    this.qtyInput.value = value;
    this.mainQtyInput.value = value;
    this.dispatchQtyEvents(this.mainQtyInput);
    this.updateQtyButtons();
    this.isSyncingQty = false;
  }

  onQtyButtonClick(event) {
    event.preventDefault();
    const isPlus = event.currentTarget === this.qtyPlusBtn;
    const currentValue = parseInt(this.qtyInput.value, 10) || this.minQty;
    let newValue = isPlus ? currentValue + this.stepQty : currentValue - this.stepQty;
    if (this.maxQty !== null) newValue = Math.min(newValue, this.maxQty);
    newValue = Math.max(newValue, this.minQty);
    if (newValue !== currentValue) {
      this.qtyInput.value = newValue;
      this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.updateQtyButtons();
  }

  initQuantitySelector() {
    if (!this.qtyInput || !this.mainQtyInput) return;

    this.qtyPlusBtn?.removeEventListener('click', this.onQtyButtonClick);
    this.qtyMinusBtn?.removeEventListener('click', this.onQtyButtonClick);
    this.qtyInput.removeEventListener('change', this.onQtyInputChange);
    this.mainQtyInput?.removeEventListener('change', this.onMainQtyChange);

    const sourceInput = this.mainQtyInput;
    this.minQty = sourceInput.min ? parseInt(sourceInput.min, 10) : 1;
    this.maxQty = sourceInput.max ? parseInt(sourceInput.max, 10) : null;
    this.stepQty = sourceInput.step ? parseInt(sourceInput.step, 10) : 1;

    this.qtyPlusBtn?.addEventListener('click', this.onQtyButtonClick);
    this.qtyMinusBtn?.addEventListener('click', this.onQtyButtonClick);
    this.qtyInput.addEventListener('change', this.onQtyInputChange);
    this.mainQtyInput?.addEventListener('change', this.onMainQtyChange);

    this.qtyInput.value = this.mainQtyInput.value;
    this.updateQtyButtons();
  }

  handleFooterIntersection(entries) {
    const [entry] = entries;
    if (!entry) return;
    this.isFooterVisible = entry.isIntersecting;
    this.updateStickyVisibility();
  }

  handleMediaIntersection(entries) {
    const [entry] = entries;
    if (!entry) return;

    this.isMediaPastThreshold = !entry.isIntersecting || entry.intersectionRatio <= 0.2;
    this.updateStickyVisibility();
  }

  syncStickyAtcTextAndPrice() {
    if (!this.mainProductForm || !this.stickyAtcText || !this.stickyAtcPrice) return;

    const mainLabel = this.mainProductForm.querySelector('.add-to-cart-label');
    const mainPrice = this.mainProductForm.querySelector('.add-to-cart-total');

    if (!mainLabel || !mainPrice) return;

    // Initial sync
    this.stickyAtcText.textContent = mainLabel.textContent.trim();

    const currentText = mainPrice.textContent.trim();
    const isSubscription = this.isSubscriptionSelected();
    const compareText = this.comparePriceText;

    this.setStickyPriceUI({ currentText, isSubscription, compareText });

    // Avoid duplicate observers
    this.atcTextObserver?.disconnect();
    this.atcTextObserver = null;

    const updateSticky = () => {
      this.stickyAtcText.textContent = mainLabel.textContent.trim();

      const nextCurrentText = mainPrice.textContent.trim();
      const nextIsSubscription = this.isSubscriptionSelected();
      const nextCompareText = this.comparePriceText;

      this.setStickyPriceUI({ currentText: nextCurrentText, isSubscription: nextIsSubscription, compareText: nextCompareText });
    };

    this.atcTextObserver = new MutationObserver(updateSticky);

    this.atcTextObserver.observe(mainLabel, {
      childList: true,
      subtree: true,
    });

    this.atcTextObserver.observe(mainPrice, {
      childList: true,
      subtree: true,
    });
  }

  onSellingPlanInputChange() {
    this.syncStickyAtcTextAndPrice();
  }


  updateMainProductColorVariant(event) {
    if (!event) return;
    const currentBtn = event.target.closest("button");
    if (!currentBtn || currentBtn.classList.contains("is-active")) return;

    // Only toggle active state within the same option group (flavor vs size)
    const optionPosition = currentBtn.getAttribute('data-option-position');
    const groupSelector = optionPosition
      ? `.sticky-atc__variant-pill[data-option-position="${optionPosition}"]`
      : '.sticky-atc__variant-pill';

    this.querySelectorAll(groupSelector).forEach(p => p.classList.remove('is-active'));
    currentBtn.classList.add('is-active');

    const inputId = currentBtn.getAttribute("data-input-id");
    const optionValue = currentBtn.textContent?.trim() || inputId;

    window.__stickyAtcOrigin = 'variant';
    clearTimeout(this._variantFlagTimer);
    this._variantFlagTimer = setTimeout(function () { window.__stickyAtcOrigin = null; }, 4000);

    const targetLabel = this.mainProductVariantSelector?.querySelector(`label[for='${inputId}']`);
    targetLabel?.click();

    this.pushDL('sticky_variant_change', Object.assign(this.getProductData(), { option_value: optionValue }));
  }

  updateProductInfo(event) {
    if (window.__stickyAtcOrigin === 'variant') {
      clearTimeout(this._variantFlagTimer);
      window.__stickyAtcOrigin = null;
    }

    if (!this.isActive) return;
    if (event?.detail?.sectionId && this.sectionId && event.detail.sectionId !== this.sectionId) return;

    this.cacheElements();
    this.initMediaObserver();
    this.initQuantitySelector();
    const variant = event?.detail?.variant;
    if (variant && String(variant.id) === String(this.mainProductForm?.querySelector('input[name="id"]')?.value || '')) {
      this.comparePriceText = this.getCompareTextFromVariant({
        price: typeof variant.price === 'number' ? variant.price : null,
        compareAtPrice: typeof variant.compare_at_price === 'number' ? variant.compare_at_price : null,
      });
    } else {
      this.comparePriceText = this.getCompareTextFromDataset();
    }

    this.syncStickyAtcTextAndPrice();
    this.syncStickyOptionPillsFromMainSelector();

    const updatedHtml = event?.detail?.html;
    if (!updatedHtml || !this.sectionId) return;

    const currentSelectedInput = this.mainProductVariantSelector?.querySelector(".product-form__input-flavor .pill-radio:checked");
    const inputId = currentSelectedInput?.getAttribute("id");
    if (currentSelectedInput && inputId) {
      const targetSwatchButton = this.querySelector(`button[data-input-id='${inputId}']`);
      if (targetSwatchButton && !targetSwatchButton.classList.contains('is-active')) {
        const optionPosition = targetSwatchButton.getAttribute('data-option-position');
        const groupSelector = optionPosition ? `.sticky-atc__variant-pill[data-option-position="${optionPosition}"]` : '.sticky-atc__variant-pill';
        this.querySelectorAll(groupSelector).forEach(p => p.classList.remove('is-active'));
        targetSwatchButton.classList.add('is-active');
      }
    }

    const srcLabel = updatedHtml?.querySelector(`#sticky-label-${this.sectionId}`);
    const destLabel = this.querySelector(`#sticky-label-${this.sectionId}`);
    const srcTitle = updatedHtml?.querySelector(`#sticky-title-${this.sectionId}`);
    const destTitle = this.querySelector(`#sticky-title-${this.sectionId}`);

    if (srcTitle && destTitle && this.isThisBundle) destTitle.innerHTML = srcTitle.innerHTML;
    if (srcLabel && destLabel && !this.isThisBundle) destLabel.innerHTML = srcLabel.innerHTML;

    this.stickyAddToCartBtn?.removeEventListener('click', this.onStickyAddToCartClick);
    this.stickyAddToCartBtn?.addEventListener('click', this.onStickyAddToCartClick);
  }

  onCartRequestEnd(e) {
    if (!this.isStickyAtcInFlight) return;

    const { requestState } = e.detail || {};
    if (requestState?.requestType !== 'add') return;

    this.isStickyAtcInFlight = false;

    if (requestState.responseData?.ok) {
      var body = requestState.responseData.body || {};
      this.pushDL('sticky_atc_success', {
        product_id: this.productId,
        product_title: body.product_title || this.productTitle,
        variant_id: body.variant_id || null,
        variant_title: body.variant_title || null,
        price: typeof body.price === 'number' ? this.formatMoney(body.price) : null,
      });
    } else {
      var errBody = requestState.responseData?.body || {};
      this.pushDL('sticky_atc_error', Object.assign(this.getProductData(), this.getVariantData(), {
        error_message: errBody.description || errBody.message || 'Unknown error',
      }));
    }

    queueMicrotask(function () { window.__stickyAtcOrigin = null; });
  }

  onStickyAddToCartClick(event) {
    event.preventDefault();
    if (!this.mainAddToCartBtn || this.mainAddToCartBtn.disabled) return;

    if (this.qtyInput && this.mainQtyInput) {
      this.mainQtyInput.value = this.qtyInput.value;
      this.dispatchQtyEvents(this.mainQtyInput);
    }

    this.pushDL('sticky_atc_click', Object.assign(this.getProductData(), this.getVariantData()));
    window.__stickyAtcOrigin = 'atc';
    this.isStickyAtcInFlight = true;

    this.mainAddToCartBtn.click();
  }
}

customElements.define('sticky-atc', StickyATC);